import React from 'react'

import { Box, Divider, Popover, Tag, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import {
  ClockInInconsistencies,
  Inconsistencies,
  TTInconsistencies,
  isClockInInconsistencies,
  isTTInconsistencies,
} from 'modules/attendance/hooks/useShiftInconsistencies'
import { AlertType, PopoverTypeToShow } from 'modules/attendance/lib/types'

type AlertTypes = {
  [key: string]: string
}

const AlertTagContent = ({
  alertTotalCounts,
  setHover,
}: {
  alertTotalCounts: number
  setHover: (value: boolean) => void
}) => {
  return (
    <Box
      maxWidth="fit-content"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Tag
        type="radical"
        size="normal"
        leftItem="icon"
        icon="alert-circle"
        iconColor="radicalRed1000"
      >
        <Text size="200" weight="medium" color="radicalRed1200">
          {alertTotalCounts}
        </Text>
      </Tag>
    </Box>
  )
}

interface AlertTypeCountProps {
  alertTypes: AlertTypes
  inconsistencies: ClockInInconsistencies | TTInconsistencies
}

const AlertTypeCount = ({ alertTypes, inconsistencies }: AlertTypeCountProps) => {
  if (!isTTInconsistencies(inconsistencies)) return <></>

  return (
    <Box textAlign="left" gap="s16">
      <Text size="200" weight="medium" color="textInverse">
        {i18n.t('time_tracking.table.inconsistencies.tooltips.alert_types.title')}
      </Text>
      <Box gap="s8">
        {Object.keys(inconsistencies).map((inconsistency, index) => {
          if (!!alertTypes[inconsistency] && !!inconsistencies[inconsistency])
            return (
              <Box flexDirection="row" justifyContent="spaceBetween" key={index}>
                <Text size="200" weight="semibold" color="textInverse">
                  {alertTypes[inconsistency]}
                </Text>
                <Text size="200" weight="normal" color="textInverse">
                  {inconsistencies[inconsistency]}
                </Text>
              </Box>
            )
        })}
      </Box>
    </Box>
  )
}

const Line = () => {
  return (
    <Box paddingY="s8">
      <Divider backgroundColor="grey700" />
    </Box>
  )
}

interface InfoProps {
  type: string
  workedHour: number
  duration: number
}

const alertInfoDescription = ({ type, workedHour, duration }: InfoProps) => {
  switch (type) {
    case AlertType.missingClockIn:
      return i18n.t('clock_in.inconsistencies.tooltips.missing_clock_in.description')
    case AlertType.missingClockOut:
      return i18n.t('clock_in.inconsistencies.tooltips.missing_clock_out.description')
    case AlertType.missingBreakTime:
      return i18n.t('clock_in.inconsistencies.tooltips.missing_break_time.description', {
        worked_hour: workedHour,
        break_duration: duration,
      })
    case AlertType.insufficientBreakDuration:
      return i18n.t('clock_in.inconsistencies.tooltips.insufficient_break_duration.description', {
        break_duration: duration,
      })
  }
}

interface AlertTypeInfoProps {
  alertTypes: AlertTypes
  inconsistencies: Inconsistencies
}

const AlertTypeInfo = ({ alertTypes, inconsistencies }: AlertTypeInfoProps) => {
  if (!isClockInInconsistencies(inconsistencies)) return <></>

  const { inconsistencyTypes } = inconsistencies ?? {}
  const inconsistencyTypesCount = inconsistencyTypes?.length

  return (
    <Box textAlign="left">
      {inconsistencyTypes.map((inconsistency, index) => {
        const { inconsistencyType, workedHour, duration } = inconsistency
        const isLastItem = index === inconsistencyTypesCount - 1

        if (!!alertTypes[inconsistencyType])
          return (
            <Box key={index}>
              <Box gap="s4">
                <Text size="200" weight="semibold" color="textInverse">
                  {alertTypes[inconsistencyType]}
                </Text>
                <Text size="200" weight="medium" color="textInverse">
                  {alertInfoDescription({ type: inconsistencyType, workedHour, duration })}
                </Text>
              </Box>
              {!isLastItem && <Line />}
            </Box>
          )
      })}
    </Box>
  )
}

const AlertTagPopover = ({
  alertTypes,
  popoverTypeToShow,
  inconsistencies,
}: {
  alertTypes: AlertTypes
  popoverTypeToShow: PopoverTypeToShow
  inconsistencies: Inconsistencies
}) => {
  if (popoverTypeToShow === PopoverTypeToShow.Info) {
    return <AlertTypeInfo inconsistencies={inconsistencies} alertTypes={alertTypes} />
  }

  return <AlertTypeCount inconsistencies={inconsistencies} alertTypes={alertTypes} />
}

interface AlertCountsTagProps {
  popoverTypeToShow: PopoverTypeToShow
  inconsistencies?: Inconsistencies
  page: string
  timeRange: string
}

const InconsistenciesAlertCountsTag = ({
  popoverTypeToShow,
  inconsistencies,
  page,
  timeRange,
}: AlertCountsTagProps) => {
  const [hover, setHover] = React.useState<boolean>(false)
  const noToggle = () => () => undefined

  if (!inconsistencies?.total) {
    if (popoverTypeToShow === PopoverTypeToShow.Count)
      return (
        <Text.Normal color="grey600" size="200">
          -
        </Text.Normal>
      )
    return null
  }

  const alertTypes: AlertTypes = {
    ['missing_clock_in']: i18n.t('clock_in.inconsistencies.tooltips.missing_clock_in.title'),
    ['missing_clock_out']: i18n.t('clock_in.inconsistencies.tooltips.missing_clock_out.title'),
    ['missing_break_time']: i18n.t('clock_in.inconsistencies.tooltips.missing_break_time.title'),
    ['insufficient_break_duration']: i18n.t(
      'clock_in.inconsistencies.tooltips.insufficient_break_duration.title'
    ),
  }

  return (
    <Box
      onMouseEnter={() => {
        setHover(true)
        track(trackingEvents.timeTracking.hoverInconsistencyTooltip, {
          page,
          time_range: timeRange,
        })
      }}
      onMouseLeave={() => setHover(false)}
      cursor="pointer"
    >
      <Popover.ItemList
        attachment="bottom center"
        targetAttachment="top center"
        open={hover}
        toggle={noToggle}
      >
        <AlertTagContent alertTotalCounts={inconsistencies?.total} setHover={setHover} />
        {!!inconsistencies && (
          <AlertTagPopover
            popoverTypeToShow={popoverTypeToShow}
            inconsistencies={inconsistencies}
            alertTypes={alertTypes}
          />
        )}
      </Popover.ItemList>
    </Box>
  )
}

export default observer(InconsistenciesAlertCountsTag)
